import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/ui/PageHeader';
import type { CategoriaProductoRow, EstadoGeneral } from '../../types/database.types';

interface FormState {
  categoria_id: string;
  codigo: string;
  nombre: string;
  marca: string;
  unidad_medida: string;
  costo: string;
  precio_venta: string;
  stock_minimo: string;
  estado: EstadoGeneral;
}

const VACIO: FormState = {
  categoria_id: '',
  codigo: '',
  nombre: '',
  marca: '',
  unidad_medida: '',
  costo: '0',
  precio_venta: '0',
  stock_minimo: '0',
  estado: 'activo',
};

/**
 * Alta y edición de productos (DAF v2, sección 15). A propósito NO tiene
 * un campo para editar `stock_actual` directamente — el stock solo
 * cambia a través de movimientos registrados en el módulo de Inventario
 * (entradas, ventas, ajustes…), para no perder la trazabilidad que exige
 * la regla de negocio 25 del DAF v2. En edición se muestra el stock
 * actual de solo lectura, con un enlace al módulo de Inventario.
 */
export function ProductoForm() {
  const { id } = useParams<{ id: string }>();
  const esNuevo = id === 'nuevo';
  const navigate = useNavigate();
  const { perfil } = useAuth();

  const [categorias, setCategorias] = useState<CategoriaProductoRow[]>([]);
  const [stockActual, setStockActual] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(VACIO);
  const [loading, setLoading] = useState(!esNuevo);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('categorias_productos')
      .select('*')
      .order('nombre')
      .then(({ data }) => data && setCategorias(data));
  }, []);

  useEffect(() => {
    if (esNuevo || !id) return;

    supabase
      .from('productos')
      .select('*')
      .eq('id', id)
      .single()
      .then(({ data, error: err }) => {
        if (err || !data) {
          setError(err?.message ?? 'No se encontró el producto');
        } else {
          setForm({
            categoria_id: data.categoria_id ?? '',
            codigo: data.codigo,
            nombre: data.nombre,
            marca: data.marca ?? '',
            unidad_medida: data.unidad_medida ?? '',
            costo: String(data.costo),
            precio_venta: String(data.precio_venta),
            stock_minimo: String(data.stock_minimo),
            estado: data.estado,
          });
          setStockActual(data.stock_actual);
        }
        setLoading(false);
      });
  }, [esNuevo, id]);

  function actualizar<K extends keyof FormState>(campo: K, valor: FormState[K]) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    // useParams() tipa `id` como `string | undefined` aunque la ruta
    // siempre lo trae ('nuevo' o un uuid real) -- este guard se lo
    // confirma a TypeScript para que `.eq('id', id)` más abajo compile
    // con `id: string` en vez de `string | undefined`.
    if (!id) return;
    setGuardando(true);
    setError(null);

    const payload = {
      salon_id: perfil.salon_id,
      categoria_id: form.categoria_id || null,
      codigo: form.codigo,
      nombre: form.nombre,
      marca: form.marca || null,
      unidad_medida: form.unidad_medida || null,
      costo: Number(form.costo),
      precio_venta: Number(form.precio_venta),
      stock_minimo: Number(form.stock_minimo),
      estado: form.estado,
    };

    // stock_actual solo se toca al crear (arranca en 0 — el primer
    // movimiento real se registra desde Inventario, ver el bloque de
    // arriba). En edición nunca se envía, para no pisar el valor
    // mantenido por los movimientos.
    const { error: err } = esNuevo
      ? await supabase.from('productos').insert({ ...payload, stock_actual: 0 })
      : await supabase.from('productos').update(payload).eq('id', id);

    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    navigate('/admin/productos');
  }

  if (loading) return <p className="text-sm text-onyx/50">Cargando…</p>;

  return (
    <div className="max-w-2xl">
      <PageHeader title={esNuevo ? 'Nuevo producto' : 'Editar producto'} />

      {!esNuevo && stockActual !== null && (
        <div className="card mb-4 flex items-center justify-between bg-cream">
          <div>
            <p className="text-sm text-onyx/60">Stock actual</p>
            <p className="font-display text-2xl text-gold-deep">{stockActual}</p>
          </div>
          <Link to="/admin/inventario" className="btn-secondary text-xs">
            Registrar movimiento →
          </Link>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Campo label="Nombre" required>
            <input
              required
              className="input-field"
              value={form.nombre}
              onChange={(e) => actualizar('nombre', e.target.value)}
            />
          </Campo>
          <Campo label="Código" required>
            <input
              required
              className="input-field"
              value={form.codigo}
              onChange={(e) => actualizar('codigo', e.target.value)}
            />
          </Campo>
        </div>

        <Campo label="Categoría">
          <select
            className="input-field"
            value={form.categoria_id}
            onChange={(e) => actualizar('categoria_id', e.target.value)}
          >
            <option value="">Sin categoría</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </Campo>

        <div className="grid grid-cols-2 gap-4">
          <Campo label="Marca">
            <input
              className="input-field"
              value={form.marca}
              onChange={(e) => actualizar('marca', e.target.value)}
            />
          </Campo>
          <Campo label="Unidad de medida">
            <input
              className="input-field"
              placeholder="unidad, ml, gr…"
              value={form.unidad_medida}
              onChange={(e) => actualizar('unidad_medida', e.target.value)}
            />
          </Campo>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Campo label="Costo (COP)">
            <input
              type="number"
              min={0}
              step={100}
              className="input-field"
              value={form.costo}
              onChange={(e) => actualizar('costo', e.target.value)}
            />
          </Campo>
          <Campo label="Precio de venta (COP)" required>
            <input
              required
              type="number"
              min={0}
              step={100}
              className="input-field"
              value={form.precio_venta}
              onChange={(e) => actualizar('precio_venta', e.target.value)}
            />
          </Campo>
          <Campo label="Stock mínimo">
            <input
              type="number"
              min={0}
              className="input-field"
              value={form.stock_minimo}
              onChange={(e) => actualizar('stock_minimo', e.target.value)}
            />
          </Campo>
        </div>

        <Campo label="Estado">
          <select
            className="input-field max-w-xs"
            value={form.estado}
            onChange={(e) => actualizar('estado', e.target.value as EstadoGeneral)}
          >
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </select>
        </Campo>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/productos')}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function Campo({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-onyx/80">
        {label}
        {required && <span className="text-gold-deep"> *</span>}
      </span>
      {children}
    </label>
  );
}
